import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReachCampaignDetails,
  SourcingCampaignsIndexResponse,
  SourcingPurchaseDetails,
  SourcingService,
} from 'api-clients/monolith';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { CampaignParameters } from '@/components/pages/edit-campaign/types';
import { useEditCampaignForm } from '@/components/pages/edit-campaign/use-edit-campaign-form';
import { useEditCampaignSchema } from '@/components/pages/edit-campaign/use-edit-campaign-schema';
import {
  HiringTarget,
  useHiringTarget,
} from '@/components/pages/edit-campaign/use-hiring-target';
import {
  ReachSummary,
  useReachSummary,
} from '@/components/pages/edit-campaign/use-reach-summary';
import { resolveMetricsApiDateParams } from '@/components/pages/opening-details/overview/date-range-view/filter-utils';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { useHiringGoalIdParam } from '@/hooks/use-hiring-goal-id-param';
import { useMetricsFilterParams } from '@/hooks/use-metrics-filter-params';
import { queryKeys } from '@/query-keys';
import { isFreeChannel } from '@/utils/channel-util';
import { getTargetApplicants, getTargetHires } from '@/utils/targets';

type ActiveEditField =
  | 'target_applicants'
  | 'expected_cpa'
  | 'expected_conversion'
  | 'target_hires'
  | 'budget'
  | null;

export interface CampaignOpening {
  openingName: string;
  openingId: string;
}

interface EditCampaignContextType {
  sourcingPurchaseId: string;
  /**
   * The campaign's opening, read from the All Campaigns list already in the
   * cache. The campaign details response has no opening fields, so this is
   * undefined when the editor opens from anywhere else.
   */
  campaignOpening?: CampaignOpening;
  hookForm: UseFormReturn<CampaignParameters>;
  onFormSubmit: () => void;
  isSubmitting: boolean;
  campaign?: SourcingPurchaseDetails;
  selectedPublisher?: ReachCampaignDetails;
  setSelectedPublisher: (publisher?: ReachCampaignDetails) => void;
  hiringTarget: HiringTarget;
  reachSummary: ReachSummary;
  isFetching: boolean;
  closeForm: () => void;
  isFormDirty: boolean;
  isReach: boolean;
  activeEditField: ActiveEditField;
  setActiveEditField: (field: ActiveEditField) => void;
  isCompleted: boolean;
  isScheduled: boolean;
  isFree: boolean;
  /**
   * True when the editor should surface hiring-goal dates / "Hiring target
   * budget". False on the all-campaigns page (and when the campaign has no
   * goal) so the UI matches the no-hiring-goal treatment.
   */
  hasHiringGoal: boolean;
}

const EditCampaignContext = createContext<EditCampaignContextType | undefined>(
  undefined,
);

export function EditCampaignProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { sourcingPurchaseId, channelSlug } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const [searchParams] = useSearchParams();
  const isReach = pathname.includes('reach');
  // All-campaigns edit outlet (`/:accountSlug/campaigns/.../edit`) is not
  // scoped to an opening's hiring goal — treat it like "no hiring goal".
  const isFromAllCampaigns = pathname.includes('/campaigns/');
  const navigate = useNavigate();
  const [selectedPublisher, setSelectedPublisher] = useState<
    ReachCampaignDetails | undefined
  >();
  const [activeEditField, setActiveEditField] = useState<ActiveEditField>(null);
  const { hiringGoalId: currentHiringGoalId, withHiringGoalId } =
    useHiringGoalIdParam();
  const { startDate, endDate, filterRange } = useMetricsFilterParams();
  const localDays = useLocalDateRanges();
  const metricsDates = useMemo(
    () =>
      resolveMetricsApiDateParams({
        startDate,
        endDate,
        filterRange,
        localDays,
      }),
    [startDate, endDate, filterRange, localDays],
  );

  if (!sourcingPurchaseId) {
    throw new Error('Sourcing purchase ID is required');
  }

  const closeForm = useCallback(() => {
    const returnTo = (
      location.state as { returnTo?: { pathname: string; search: string } }
    )?.returnTo;
    if (returnTo?.pathname) {
      navigate(returnTo);
      return;
    }

    const path = pathname.split('/').slice(0, -3).join('/');

    navigate({
      pathname: path,
      search: withHiringGoalId(searchParams),
    });
  }, [location.state, pathname, searchParams, withHiringGoalId, navigate]);

  const queryClient = useQueryClient();
  const campaignOpening = useMemo<CampaignOpening | undefined>(() => {
    const row = queryClient
      .getQueriesData<SourcingCampaignsIndexResponse>({
        queryKey: queryKeys.allCampaigns,
      })
      .flatMap(([, data]) => data?.campaigns ?? [])
      .find(({ id }) => id === sourcingPurchaseId);
    return row
      ? { openingName: row.opening_name, openingId: row.opening_external_id }
      : undefined;
  }, [queryClient, sourcingPurchaseId]);

  // Fetch the campaign details.
  const {
    data: campaign,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.campaignDetails,
      sourcingPurchaseId,
      currentHiringGoalId,
      metricsDates.startDate,
      metricsDates.endDate,
      metricsDates.filterRange,
    ],
    queryFn: () => {
      return SourcingService.getInternalApiSourcingSourcingPurchases(
        sourcingPurchaseId,
        currentHiringGoalId,
        metricsDates.startDate,
        metricsDates.endDate,
        metricsDates.filterRange,
      );
    },
    enabled: !!sourcingPurchaseId,
  });

  if (isError) {
    throw new Error('Failed to fetch campaign details');
  }

  const isFree =
    !!campaign &&
    isFreeChannel(
      campaign.sourcing_channel,
      campaign.budget === 0,
      campaign.contract_id == null,
    );

  const validationSchema = useEditCampaignSchema({
    channelSlug,
    budgetSpent: campaign?.budget_spent,
    isCompleted: campaign?.status === 'completed',
    isReadonly: campaign?.is_readonly,
    isFree,
  });

  const hookForm = useForm<CampaignParameters>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });
  const { setValue, formState, watch } = hookForm;

  useEffect(() => {
    if (isReach && campaign) {
      setSelectedPublisher(campaign.reach_campaigns?.[0]);
    }
  }, [campaign, setSelectedPublisher, isReach]);

  const hiringGoalSpend =
    (isReach ? selectedPublisher?.budget_spent : campaign?.budget_spent) ?? 0;
  const preHiringGoalSpend =
    (isReach
      ? selectedPublisher?.pre_hiring_goal_spend
      : campaign?.pre_hiring_goal_spend) ?? 0;
  const durationStats = isReach
    ? selectedPublisher?.duration_stats
    : campaign?.duration_stats;

  const hasHiringGoal =
    !isFromAllCampaigns && !!campaign?.hiring_goal?.external_id;

  // Calculate the hiring target. Omit the goal on the all-campaigns page so
  // projections use full campaign dates (same as no hiring goal).
  const hiringTarget = useHiringTarget({
    hookForm,
    hiringGoalSpend,
    hiringGoal: hasHiringGoal
      ? (campaign?.hiring_goal ?? undefined)
      : undefined,
    durationStats,
    isIntegratedChannel: campaign?.sourcing_channel.is_integratable ?? false,
    preHiringGoalSpend,
  });

  // Calculate the reach summary.
  const reachSummary = useReachSummary({
    hiringTarget,
    campaign,
    selectedPublisher,
    hookForm,
  });

  // Initialize the form.
  const { onFormSubmit, isSubmitting } = useEditCampaignForm({
    sourcingPurchaseId,
    reachCampaignId: selectedPublisher?.reach_campaign_id,
    hookForm,
    isReach,
    hiringTarget,
    reachSummary,
    closeForm,
    budgetBefore: campaign?.budget,
    channel: campaign?.sourcing_channel.slug,
    campaignOpening,
  });

  const isFormDirty = useMemo(() => {
    const isDirty =
      Object.keys(
        Object.entries(formState.dirtyFields).filter(
          ([key]) => key !== 'target_applicants' && key !== 'target_hires',
        ),
      ).length > 0;

    return isDirty;
  }, [formState]);

  const [targetCpa, targetConversion] = watch([
    'expected_cpa',
    'expected_conversion',
  ]);

  // Update target applicants and hires when hiringTarget.targetBudget changes
  useEffect(() => {
    if (!hiringTarget.targetBudget) return;

    if (activeEditField !== 'target_applicants')
      setValue(
        'target_applicants',
        getTargetApplicants(hiringTarget.targetBudget, targetCpa),
      );

    if (activeEditField !== 'target_hires')
      setValue(
        'target_hires',
        getTargetHires(hiringTarget.targetBudget, targetConversion, targetCpa),
      );
  }, [
    hiringTarget.targetBudget,
    setValue,
    activeEditField,
    targetCpa,
    targetConversion,
  ]);

  const isCompleted = useMemo(() => {
    return isReach
      ? selectedPublisher?.status === 'completed'
      : campaign?.status === 'completed';
  }, [campaign?.status, selectedPublisher?.status, isReach]);

  const isScheduled = useMemo(() => {
    return isReach
      ? selectedPublisher?.status === 'scheduled'
      : campaign?.status === 'scheduled';
  }, [campaign?.status, selectedPublisher?.status, isReach]);

  return (
    <EditCampaignContext.Provider
      value={{
        sourcingPurchaseId,
        campaignOpening,
        hookForm,
        onFormSubmit,
        isSubmitting,
        campaign,
        selectedPublisher,
        setSelectedPublisher,
        hiringTarget,
        reachSummary,
        isFetching,
        closeForm,
        isFormDirty,
        isReach,
        activeEditField,
        setActiveEditField,
        isCompleted,
        isScheduled,
        isFree,
        hasHiringGoal,
      }}
    >
      {children}
    </EditCampaignContext.Provider>
  );
}

export function useEditCampaignContext() {
  const context = useContext(EditCampaignContext);
  if (!context) {
    throw new Error(
      'useEditCampaignContext must be used within an EditCampaignProvider',
    );
  }
  return context;
}
